/**
 * pages/StudiesPage.tsx — CLI-451
 * Список исследований пациента (МРТ, КТ, УЗИ, ЭКГ, ...). Read-only.
 */
import { useQuery } from '@tanstack/react-query';
import { getStudies } from '../services/patient.service';

const TYPE_LABELS: Record<string, string> = {
  MRI: 'МРТ', CT: 'КТ', XRAY: 'Рентген', ULTRASOUND: 'УЗИ',
  ECG: 'ЭКГ', ECHO: 'ЭхоКГ', ENDOSCOPY: 'Эндоскопия', OTHER: 'Другое',
};

const STATUS_LABELS: Record<string, string> = {
  ORDERED: 'Назначено', PERFORMED: 'Выполнено', DESCRIBED: 'Описано', CANCELLED: 'Отменено',
};

const STATUS_COLORS: Record<string, string> = {
  ORDERED: 'bg-blue-100 text-blue-700',
  PERFORMED: 'bg-amber-100 text-amber-700',
  DESCRIBED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function StudiesPage() {
  const { data: studies, isLoading, isError } = useQuery({
    queryKey: ['patient-studies'],
    queryFn: getStudies,
  });

  if (isLoading) return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl h-24 animate-pulse" />
      ))}
    </div>
  );

  if (isError) return (
    <div className="p-4 text-center text-red-500">Ошибка загрузки исследований.</div>
  );

  if (!studies?.length) return (
    <div className="p-4 text-center text-gray-500 mt-12">
      <p className="text-4xl mb-3">🩻</p>
      <p className="font-medium text-gray-700">Исследований пока нет</p>
    </div>
  );

  return (
    <div className="p-4 space-y-3">
      <h2 className="text-lg font-bold text-gray-800">Мои исследования</h2>
      {studies.map((s) => (
        <div key={s.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="font-semibold text-gray-800">{TYPE_LABELS[s.type] ?? s.type}</p>
              <p className="text-xs text-gray-500">{formatDate(s.performedAt ?? s.createdAt)}</p>
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] ?? 'bg-gray-100 text-gray-500'}`}>
              {STATUS_LABELS[s.status] ?? s.status}
            </span>
          </div>
          {s.indication && (
            <p className="text-sm text-gray-700 mb-1"><span className="font-medium">Показание: </span>{s.indication}</p>
          )}
          {s.description && (
            <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2 mt-2">{s.description}</p>
          )}
          {s.radiologistName && (
            <p className="text-xs text-gray-400 mt-2">Врач: {s.radiologistName}</p>
          )}
        </div>
      ))}
    </div>
  );
}
